import React from "react";
import { Mail, Phone, MapPin, Trash2, FileText, Download, CheckCircle2 } from "lucide-react";

const LEAD_STATUSES = ["new", "contacted", "quoted", "won", "lost"];

// Keys we already render explicitly so we hide them from the "extras" dump
const SUBCONTRACTOR_RENDERED_KEYS = new Set([
  "_id", "id", "created_at", "status", "website",
  "company_name", "dba_trade_name",
  "contact_first_name", "contact_middle_name", "contact_last_name", "role_title",
  "phone", "email", "website_url",
  "business_street1", "business_street2", "business_city", "business_state", "business_zip", "business_country",
  "company_id_vendor_number",
  "service_areas_covered", "service_areas_other", "max_travel_radius_miles", "travel_limitations",
  "trades_offered", "trades_other",
  "pricing_basis", "trade_pricing", "pricing_notes",
  "crew_size", "num_crews", "project_types", "capacity_limits", "team_notes",
  "current_availability", "preferred_start", "scheduling_constraints",
  "w9_file", "coi_file", "wc_file", "id_file",
  "payment_methods", "payment_methods_other",
  "signer_first_name", "signer_middle_name", "signer_last_name",
  "typed_signature", "signature_date", "esign_acknowledged",
  // legacy fields we silently hide from the new admin display
  "trade", "contact_name", "service_areas", "years_in_business",
  "insured", "licensed", "availability", "message",
]);

const GENERIC_HIDDEN_KEYS = new Set([
  "id", "name", "company_name", "phone", "email", "address",
  "created_at", "website", "_id",
]);

function joinName(parts) {
  return parts.filter(Boolean).join(" ").trim();
}

function joinAddress(record) {
  return [
    record.business_street1,
    record.business_street2,
    [record.business_city, record.business_state, record.business_zip].filter(Boolean).join(", "),
    record.business_country,
  ].filter(Boolean).join(" · ");
}

function SectionBlock({ title, children }) {
  if (!children) return null;
  return (
    <div className="border-t border-gray-100 pt-3">
      <div className="text-[10px] uppercase tracking-[0.3em] text-br-bronze font-bold mb-2">{title}</div>
      {children}
    </div>
  );
}

function KvRow({ label, value }) {
  if (value === null || value === undefined || value === "" || value === false) return null;
  let rendered = value;
  if (Array.isArray(value)) rendered = value.join(", ");
  return (
    <div className="grid grid-cols-3 gap-2 text-xs py-0.5">
      <div className="text-gray-500 uppercase tracking-widest col-span-1">{label}</div>
      <div className="text-gray-800 col-span-2 break-words">{String(rendered)}</div>
    </div>
  );
}

function ChipList({ items }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((it) => (
        <span key={it} className="inline-block px-2 py-0.5 bg-br-paper border border-gray-200 rounded text-[11px] tracking-wide">
          {it}
        </span>
      ))}
    </div>
  );
}

function FileDownloadButton({ label, file }) {
  if (!file?.data_url) {
    return (
      <div className="border border-gray-200 rounded p-2 text-xs text-gray-400 bg-gray-50">
        {label}: not provided
      </div>
    );
  }
  const sizeKb = file.size ? Math.round(file.size / 1024) : 0;
  return (
    <a
      href={file.data_url}
      download={file.filename || `${label}.pdf`}
      className="border border-gray-200 hover:border-br-bronze rounded p-2 text-xs flex items-center gap-2 transition"
      data-testid={`download-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    >
      <FileText size={12} className="text-br-bronze shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="font-bold text-gray-700">{label}</div>
        <div className="text-gray-500 truncate" title={file.filename}>
          {file.filename || "file"} · {sizeKb}KB
        </div>
      </div>
      <Download size={12} className="text-br-bronze shrink-0" />
    </a>
  );
}

function TradePricingTable({ trade_pricing }) {
  if (!trade_pricing || typeof trade_pricing !== "object") return null;
  const entries = Object.entries(trade_pricing).filter(([, v]) => v !== "" && v !== null && v !== undefined);
  if (entries.length === 0) return <div className="text-xs text-gray-500">No trade pricing provided.</div>;
  // group by trade (key format: "<Trade>__<field>")
  const byTrade = entries.reduce((acc, [k, v]) => {
    const [trade, field] = k.split("__");
    if (!acc[trade]) acc[trade] = [];
    acc[trade].push({ field, value: v });
    return acc;
  }, {});
  return (
    <div className="space-y-2">
      {Object.entries(byTrade).map(([trade, rows]) => (
        <div key={trade} className="border border-gray-200 rounded p-2">
          <div className="text-[11px] font-bold uppercase tracking-widest text-br-bronze mb-1">{trade}</div>
          <div className="grid grid-cols-2 gap-x-3 gap-y-1">
            {rows.map((r) => (
              <div key={r.field} className="text-xs">
                <span className="text-gray-500">{r.field.replaceAll("_", " ")}: </span>
                <span className="font-bold text-gray-800">${String(r.value)}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Subcontractor renderer (rich, sectioned, with file downloads)
// ─────────────────────────────────────────────────────────────────────
function SubcontractorDetail({ record }) {
  const contactName = joinName([record.contact_first_name, record.contact_middle_name, record.contact_last_name]);
  const signerName = joinName([record.signer_first_name, record.signer_middle_name, record.signer_last_name]);
  const address = joinAddress(record);

  return (
    <div className="space-y-3 text-sm">
      <div className="space-y-2">
        {record.phone && (
          <a href={`tel:${record.phone}`} className="flex items-center gap-2 hover:text-br-bronze">
            <Phone size={14} /> {record.phone}
          </a>
        )}
        {record.email && (
          <a href={`mailto:${record.email}`} className="flex items-center gap-2 hover:text-br-bronze break-all">
            <Mail size={14} /> {record.email}
          </a>
        )}
        {address && <div className="flex items-start gap-2 text-gray-700"><MapPin size={14} className="mt-0.5 shrink-0" /> <span className="break-words">{address}</span></div>}
        {record.website_url && (
          <a href={record.website_url} target="_blank" rel="noreferrer" className="text-xs text-br-bronze hover:underline break-all">
            {record.website_url}
          </a>
        )}
      </div>

      <SectionBlock title="Company & Contact">
        <KvRow label="DBA" value={record.dba_trade_name} />
        <KvRow label="Contact" value={contactName} />
        <KvRow label="Role" value={record.role_title} />
        <KvRow label="Vendor #" value={record.company_id_vendor_number} />
      </SectionBlock>

      <SectionBlock title="Service Areas & Travel">
        <div className="mb-1"><ChipList items={record.service_areas_covered} /></div>
        <KvRow label="Other" value={record.service_areas_other} />
        <KvRow label="Max Radius" value={record.max_travel_radius_miles ? `${record.max_travel_radius_miles} mi` : ""} />
        <KvRow label="Travel Limits" value={record.travel_limitations} />
      </SectionBlock>

      <SectionBlock title="Trades">
        <div className="mb-1"><ChipList items={record.trades_offered} /></div>
        <KvRow label="Other Trades" value={record.trades_other} />
      </SectionBlock>

      <SectionBlock title="Pricing">
        <KvRow label="Pricing Basis" value={record.pricing_basis} />
        <div className="my-2"><TradePricingTable trade_pricing={record.trade_pricing} /></div>
        <KvRow label="Pricing Notes" value={record.pricing_notes} />
      </SectionBlock>

      <SectionBlock title="Crew & Capacity">
        <KvRow label="Crew Size" value={record.crew_size} />
        <KvRow label="# Crews" value={record.num_crews} />
        <div className="my-1"><ChipList items={record.project_types} /></div>
        <KvRow label="Capacity" value={record.capacity_limits} />
        <KvRow label="Team Notes" value={record.team_notes} />
      </SectionBlock>

      <SectionBlock title="Availability">
        <KvRow label="Current" value={record.current_availability} />
        <KvRow label="Start" value={record.preferred_start} />
        <KvRow label="Constraints" value={record.scheduling_constraints} />
      </SectionBlock>

      <SectionBlock title="Compliance Documents">
        <div className="grid grid-cols-2 gap-2">
          <FileDownloadButton label="W-9" file={record.w9_file} />
          <FileDownloadButton label="COI" file={record.coi_file} />
          <FileDownloadButton label="Workers Comp" file={record.wc_file} />
          <FileDownloadButton label="Government ID" file={record.id_file} />
        </div>
      </SectionBlock>

      <SectionBlock title="Payment & E-Signature">
        <div className="mb-1"><ChipList items={record.payment_methods} /></div>
        <KvRow label="Other Payment" value={record.payment_methods_other} />
        <KvRow label="Signer" value={signerName} />
        <KvRow label="Signature" value={record.typed_signature} />
        <KvRow label="Signed On" value={record.signature_date} />
        {record.esign_acknowledged && (
          <div className="flex items-center gap-1 text-xs text-emerald-700 mt-1">
            <CheckCircle2 size={12} /> E-SIGN acknowledged
          </div>
        )}
      </SectionBlock>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Generic renderer (leads, maintenance)
// ─────────────────────────────────────────────────────────────────────
function GenericDetail({ record }) {
  const extras = Object.entries(record).filter(([k, v]) =>
    !GENERIC_HIDDEN_KEYS.has(k) && v !== null && v !== "" && v !== undefined && v !== false,
  );
  return (
    <div className="space-y-2 text-sm">
      {record.phone && <a href={`tel:${record.phone}`} className="flex items-center gap-2 hover:text-br-bronze"><Phone size={14}/> {record.phone}</a>}
      {record.email && <a href={`mailto:${record.email}`} className="flex items-center gap-2 hover:text-br-bronze"><Mail size={14}/> {record.email}</a>}
      {record.address && <div className="flex items-center gap-2 text-gray-700"><MapPin size={14}/> {record.address}</div>}
      <div className="mt-4 text-sm space-y-1">
        {extras.map(([k, v]) => (
          <KvRow key={k} label={k.replaceAll("_", " ")} value={Array.isArray(v) ? v.join(", ") : v} />
        ))}
      </div>
    </div>
  );
}

function LeadStatusButtons({ active, onUpdate }) {
  return (
    <div className="mt-5 border-t border-gray-100 pt-3">
      <div className="text-xs uppercase tracking-widest text-gray-500 mb-2">Update status</div>
      <div className="flex flex-wrap gap-1">
        {LEAD_STATUSES.map((s) => (
          <button
            key={s}
            onClick={() => onUpdate(active.id, s)}
            className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded border ${active.status === s ? "bg-br-bronze text-white border-br-bronze" : "border-gray-300 hover:border-br-bronze"}`}
            data-testid={`lead-status-${s}`}
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function AdminRecordDetail({ active, tab, onDelete, onUpdateStatus }) {
  if (!active) {
    return (
      <div className="bg-white rounded border border-dashed border-gray-300 p-10 text-center text-sm text-gray-500" data-testid="admin-detail-empty">
        Select a row to view full details.
      </div>
    );
  }

  // Avoid unused-var warning for the schema we no longer dump generically
  void SUBCONTRACTOR_RENDERED_KEYS;

  const title = active.company_name || active.name || "Record";
  return (
    <div className="bg-white rounded border border-gray-200 p-5 lg:sticky lg:top-6 max-h-[calc(100vh-2rem)] overflow-y-auto" data-testid="admin-detail">
      <div className="flex items-center justify-between mb-2">
        <div className="font-display tracking-widest text-base uppercase break-words pr-2">{title}</div>
        <button onClick={() => onDelete(active.id)} className="text-xs text-red-600 hover:underline flex items-center gap-1 shrink-0" data-testid="admin-detail-delete">
          <Trash2 size={12}/> Delete
        </button>
      </div>
      <div className="text-xs text-gray-500 mb-4">
        {active.created_at && new Date(active.created_at).toLocaleString()}
        {active.status && <span className="ml-2 inline-block px-2 py-0.5 bg-br-paper border border-gray-200 rounded text-[10px] uppercase tracking-widest">{active.status}</span>}
      </div>

      {tab === "subcontractors"
        ? <SubcontractorDetail record={active} />
        : <GenericDetail record={active} />
      }

      {tab === "leads" && <LeadStatusButtons active={active} onUpdate={onUpdateStatus} />}
    </div>
  );
}
